import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import invariant from "ts-invariant";

import { useGetSmartMessageReplies } from "api/hooks/useGetSmartMessageReplies";
import { useGetConversations, usePostMessage, usePutMarkMessageAsRead } from "api/services/messaging/hooks";
import { useGetConversationMessages } from "api/services/messaging/hooks/useGetConversationMessages";
import { type MessagePayload, MessagePayloadSchema } from "api/services/messaging/schemas/messaging";
import { useGetPatientProfile } from "api/services/patient/hooks";
import { useProfileContext } from "contexts/ProfileContext";
import { useSavedInputsContext } from "contexts/SavedInputsContext";
import { useInfiniteScrollMessages } from "routes/messages/hooks/useInfiniteScrollMessages";
import type { QuickRepliesSize } from "shared/atoms/inputs/QuickReplies/quickRepliesConfig";
import { Spinner } from "shared/atoms/Spinner";
import { LeifGPT } from "shared/LeifGPT/LeifGPT";
import { AnalyticsEvents } from "utils/analytics/AnalyticsEvents";
import { reportError } from "utils/errorReporting";

import { MessagesContainer } from "../MessagesContainer";

import { ConversationHeader } from "./ConversationHeader";
import { Message } from "./Message";
import { MessageInput } from "./MessageInput";

const SMART_REPLY_POLLING_TIMEOUT_MS = 60_000;
const NO_SUGGESTION_DISPLAY_MS = 5_000;

interface ConversationProps {
  hideConversationHeader?: boolean;
  showMessageShortcuts?: boolean;
  quickRepliesSize?: QuickRepliesSize;
}

export const Conversation: React.FC<ConversationProps> = ({
  hideConversationHeader,
  showMessageShortcuts = true,
  quickRepliesSize = "default",
}) => {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const { profile } = useProfileContext();
  const { savedMessages, setSavedMessages } = useSavedInputsContext();
  const putMarkMessageAsRead = usePutMarkMessageAsRead();
  const postMessage = usePostMessage();

  invariant(patientId, "Patient ID should be defined");
  invariant(profile, "Profile should be defined");

  const form = useForm<Omit<MessagePayload, "to">>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      body: "",
    },
    resolver: zodResolver(MessagePayloadSchema.omit({ to: true })),
  });

  useEffect(() => {
    form.setValue("body", savedMessages[Number(patientId)] ?? "");
  }, [patientId]);

  useEffect(() => {
    return () => {
      const currentBody = form.getValues("body");
      setSavedMessages(prev => {
        const next = { ...prev };
        if (currentBody) {
          next[Number(patientId)] = currentBody;
        } else {
          delete next[Number(patientId)];
        }
        return next;
      });
    };
  }, [patientId]);

  const { data: patientData } = useGetPatientProfile({ patientID: Number(patientId) });

  const { data: conversationsData, refetch: refetchConversations } = useGetConversations(profile.id, {
    staleTime: Infinity,
  });
  const pinnedMessages =
    conversationsData?.conversations?.find(conversation => conversation.peer.id === Number(patientId))
      ?.pinned_messages || [];

  const {
    data: conversationMessagesData,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
    refetch: refetchMessages,
  } = useGetConversationMessages(
    {
      userID: profile.id,
      patientID: Number(patientId),
    },
    {
      refetchInterval: 30000,
      select: responseData => {
        return {
          pages: [...responseData.pages].reverse().map(page => [...page].reverse()),
          pageParams: [...responseData.pageParams].reverse(),
        };
      },
    }
  );
  const pageCount = conversationMessagesData?.pages?.length ?? 0;

  const { loadMoreRef, containerRef, scrollToBottom } = useInfiniteScrollMessages({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    pageCount,
    data: conversationMessagesData,
    resetDependency: patientId,
  });

  const allMessages = conversationMessagesData?.pages?.flat() ?? [];
  const lastMessage = allMessages[allMessages.length - 1];
  const patientSentLastMessage = lastMessage?.from?.id === Number(patientId);

  const [pollingStartedAt, setPollingStartedAt] = useState<number | null>(null);
  const [pollingExpired, setPollingExpired] = useState(false);
  const [showNoSuggestion, setShowNoSuggestion] = useState(false);

  useEffect(() => {
    setPollingStartedAt(
      patientSentLastMessage && lastMessage?.created_at ? new Date(lastMessage.created_at).getTime() : null
    );
    setPollingExpired(false);
    setShowNoSuggestion(false);
  }, [lastMessage?.id, patientSentLastMessage, patientId]);

  useEffect(() => {
    if (!pollingStartedAt) {
      return undefined;
    }
    const remaining = SMART_REPLY_POLLING_TIMEOUT_MS - (Date.now() - pollingStartedAt);
    const timer = setTimeout(() => {
      setPollingExpired(true);
      setShowNoSuggestion(true);
    }, remaining);
    return () => clearTimeout(timer);
  }, [pollingStartedAt]);

  useEffect(() => {
    if (!showNoSuggestion) {
      return undefined;
    }
    const timer = setTimeout(() => setShowNoSuggestion(false), NO_SUGGESTION_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [showNoSuggestion]);

  const { data: smartRepliesData } = useGetSmartMessageReplies(
    { therapistId: profile.id, patientId: Number(patientId) },
    {
      cacheTime: 0,
      enabled: patientSentLastMessage,
      staleTime: 0,
      refetchInterval: data => {
        if (!patientSentLastMessage || data?.message?.body) return false;
        if (pollingStartedAt && Date.now() - pollingStartedAt >= SMART_REPLY_POLLING_TIMEOUT_MS) return false;
        return 5000;
      },
    }
  );
  const smartReply = smartRepliesData?.message?.body;
  const smartReplyId = smartRepliesData?.message?.id;

  const isNearBottom = () => {
    const container = containerRef.current;
    if (!container) return false;
    return container.scrollHeight - container.scrollTop - container.clientHeight < container.clientHeight;
  };

  useEffect(() => {
    if (smartReply && isNearBottom()) {
      scrollToBottom();
    }
  }, [smartReply]);

  useEffect(() => {
    if (!conversationMessagesData) return;

    const latestUnreadMessage = conversationMessagesData.pages
      .flat()
      .filter(message => message?.from?.id === Number(patientId) && !message.read_at)[0];

    if (latestUnreadMessage?.id) {
      putMarkMessageAsRead.mutateAsync(latestUnreadMessage.id, {
        onSuccess: () => {
          refetchConversations();
          refetchMessages();
        },
      });
    }
  }, [conversationMessagesData]);

  const handleSubmitMessage = async (body: string) => {
    try {
      await postMessage.mutateAsync(
        { body, to: Number(patientId) },
        {
          onSuccess: async () => {
            await refetchConversations();
            await refetchMessages();
            scrollToBottom();
          },
        }
      );
      form.reset({ body: "" });
      setSavedMessages(prev => {
        const next = { ...prev };
        delete next[Number(patientId)];
        return next;
      });
    } catch (error) {
      reportError("Conversation handleSubmitMessage error", new Error(JSON.stringify(error)));
    }
  };

  const onSubmit = form.handleSubmit(async formData => {
    await handleSubmitMessage(formData.body);
  });

  return (
    <Stack sx={{ flex: 1, height: "100%", width: "100%", backgroundColor: "#fff" }}>
      {!hideConversationHeader && (
        <ConversationHeader
          isLoading={isLoading}
          patientID={patientId}
          patientName={patientData ? `${patientData.first_name} ${patientData.last_name}` : undefined}
          patientAvatarSrc={patientData?.avatars?.medium || undefined}
        />
      )}

      <Stack data-testid="conversation-container" ref={containerRef} sx={{ flex: 1, overflowY: "scroll" }}>
        <MessagesContainer
          isEmpty={!isLoading && (!conversationMessagesData?.pages || conversationMessagesData?.pages?.length === 0)}
          isError={isError}
          isLoading={isLoading}
        >
          <>
            <LoadMoreSentinel ref={loadMoreRef}>
              {isFetchingNextPage && <Spinner small />}
              {!hasNextPage && !isFetchingNextPage && <>{t("messages.chat_window.beginning")}</>}
            </LoadMoreSentinel>

            <Stack padding={3} spacing={2}>
              {conversationMessagesData?.pages?.map(page => {
                return page?.map(message => (
                  <Message key={message.id} message={message} pinnedMessages={pinnedMessages} userID={profile.id} />
                ));
              })}
            </Stack>

            {smartReply && smartReplyId && (
              <StyledLeifGPT
                patientId={Number(patientId)}
                smartReply={smartReply}
                smartReplyId={smartReplyId}
                onEdit={value => {
                  form.setValue("body", value);
                }}
                onSubmit={handleSubmitMessage}
                dismissEvent={AnalyticsEvents.LEIF_GPT.MESSAGES.SUGGESTION_DISMISSED}
                editEvent={AnalyticsEvents.LEIF_GPT.MESSAGES.SUGGESTION_EDITED}
                presentEvent={AnalyticsEvents.LEIF_GPT.MESSAGES.SUGGESTION_PRESENTED}
                submitEvent={AnalyticsEvents.LEIF_GPT.MESSAGES.SUGGESTION_SUBMITTED}
              />
            )}
          </>
        </MessagesContainer>
      </Stack>

      {patientSentLastMessage && !smartReply && !pollingExpired && (
        <ThinkingBar role="status" aria-live="polite">
          {t("leifgpt.thinking")}
          <ThinkingDots>
            <span />
            <span />
            <span />
          </ThinkingDots>
        </ThinkingBar>
      )}
      {showNoSuggestion && (
        <ThinkingBar role="status" aria-live="polite">
          {t("leifgpt.no_suggestion")}
        </ThinkingBar>
      )}

      <MessageInput
        form={form}
        onSubmit={onSubmit}
        quickRepliesSize={quickRepliesSize}
        slots={{ quickReplyShortcuts: showMessageShortcuts }}
      />
    </Stack>
  );
};

const LoadMoreSentinel = styled.div`
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
`;

const StyledLeifGPT = styled(LeifGPT)`
  width: auto;
  margin: 0 24px 24px;
`;

const ThinkingBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.redesign.b100};
  border-top: 1px solid ${({ theme }) => theme.colors.redesign.db10};
`;

const dotBounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  40% {
    transform: translateY(-4px);
    opacity: 1;
  }
`;

const ThinkingDots = styled.span`
  display: inline-flex;
  gap: 3px;
  align-items: center;

  span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.4;
    animation: ${dotBounce} 1.2s ease-in-out infinite;
  }

  span:nth-child(2) {
    animation-delay: 0.2s;
  }

  span:nth-child(3) {
    animation-delay: 0.4s;
  }
`;
